import React, { useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";

import { V2 } from "../Api";
import { surgeryStatusToText } from "../TextTables";
import { formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName } from "../Utilities";

import EventStream, { EventStreamListener } from "./EventStream";
import { PatientSearchInner } from "./views/PatientSearchView";
import withContentStyle from "./withContentStyle";

import Style from "./DocumentImportEditor.module.css";
import { Prompt, useHistory } from "react-router-dom";
import ProgressBar from "./ProgressBar";

type DocumentImportResults = {
    results: any[];
    status: number;
    pageCount: number;
    unidentifiedPages: number[];
    errorMessage?: string;
};

type DocumentImportDocumentList = any[];

interface IDocumentImportEditorProps {
    className?: string;
    jobId: string;
}

const DocumentImportEditor: React.FC<IDocumentImportEditorProps> = function (props) {
    const className = props.className
        ? `${props.className} ${Style.editorRoot}`
        : Style.editorRoot;

    const [isLoading, setIsLoading] = useState<boolean | string>(true);
    const [hasChanged, setHasChanged] = useState<boolean>(false);
    const [importResults, setImportResults] = useState<DocumentImportResults>({ results: [], status: 0, pageCount: 0, unidentifiedPages: [] });
    const [selectedResult, setSelectedResult] = useState<number>(0);
    const [selectedPage, setSelectedPage] = useState<number | null>(null);
    const [eventStreams, setEventStreams] = useState<EventStreamListener[]>([]);
    const [isPatientSelectionDialogOpen, setIsPatientSelectionDialogOpen] = useState<boolean>(false);
    const [isSurgerySelectionDialogOpen, setIsSurgerySelectionDialogOpen] = useState<boolean>(false);
    const [isDocumentSelectionDialogOpen, setIsDocumentSelectionDialogOpen] = useState<boolean>(false);
    const [documentSurgeryId, setDocumentSurgeryId] = useState<string>("");
    const [isAddMode, setIsAddMode] = useState<boolean>(false);
    const [movePage, setMovePage] = useState<any | null>(null);
    const [isFinishing, setIsFinishing] = useState<boolean | string>(false);
    const [isComplete, setIsComplete] = useState<boolean>(false);
    const history = useHistory();
    const loadJobRef = useRef<(data: any) => void>();
    const [progressInfo, setProgressInfo] = useState({ currentPage: 0, totalPages: 0 });

    // NOTE: Not sure if this is the best way around keeping the function identity up to date so it can
    //   use the value of 'isFinishing' from the event stream handler. It works though
    loadJobRef.current = (data: any) => {
        console.log(data, isFinishing);

        const { status } = data;
        if (status === 5) {
            // Finished
            setIsComplete(true);
            setHasChanged(false);
            setSelectedResult(0);
            setIsFinishing(false);
            setImportResults({
                results: [],
                status: 5,
                pageCount: 0,
                unidentifiedPages: []
            });
            return;
        }
        //else if (isFinishing && status !== 4) {
        //    // Don't reload documents if we get an update while finishing
        //    return;
        //}

        V2.get<DocumentImportResults>(`document/import/${props.jobId}`)
            .then(a => {
                if (a.results && !isFinishing) {
                    let counter = 1;
                    // Sort detected documents by surgery
                    for (const result of a.results) {
                        const surgeryMap: { [id: string]: any } = {};
                        for (const document of result.documents) {
                            if (!document.surgeryId) {
                                document.surgeryId = "";
                            }

                            if (!surgeryMap[document.surgeryId]) {
                                surgeryMap[document.surgeryId] = {
                                    surgeryId: document.surgeryId,
                                    date: document.surgeryDate,
                                    time: document.surgeryTime,
                                    documents: []
                                };
                            }

                            document.id = counter.toString();
                            counter++;
                            surgeryMap[document.surgeryId].documents.push(document);
                        }

                        console.log(surgeryMap);

                        result.surgeries = Object.keys(surgeryMap).map(a => surgeryMap[a]);
                        delete result.documents;
                    }

                    // Add fake "result" to show unidentified documents
                    if (a.unidentifiedPages.length > 0) {
                        a.results.unshift({
                            patient: {
                                id: "0",
                                chartNumber: "__unknown"
                            },
                            surgeries: [{
                                surgeryId: "",
                                documents: [{
                                    id: "?",
                                    documentTypeId: "?",
                                    documentName: "Unidentified",
                                    pages: a.unidentifiedPages,
                                    surgeryId: null
                                }]
                            }]
                        });
                    }

                    setImportResults(a);
                }

                if (!isFinishing && a.status === 4) {
                    // Error
                    setIsLoading(false);
                    setImportResults({
                        status: 4,
                        pageCount: 0,
                        results: [],
                        unidentifiedPages: [],
                        errorMessage: a.errorMessage
                    });
                    setIsFinishing(a.errorMessage || "Could not process document");
                    return;
                }

                if (isFinishing && a.status === 4) {
                    // Error
                    setIsFinishing(a.errorMessage || "Could not save");
                    return;
                }

                setIsLoading(false);
            })
            .catch(e => {
                if (e.statusCode === 404 && isFinishing) {
                    return;
                }

                setImportResults({ results: [], status: 0, pageCount: 0, unidentifiedPages: [] });
                setIsLoading(e.message || "Could not load document import job")
            });
    };

    useEffect(() => {
        console.log("Update job ID");
        setEventStreams([
            {
                id: `document.import.job.update.${props.jobId}`,
                handler: (event: any) => {
                    const data = JSON.parse(event.data);

                    // Call the most recent function instance
                    loadJobRef.current?.(data);
                }
            },
            {
                id: `document.import.job.progress.${props.jobId}`,
                handler: (event: any) => {
                    const data = JSON.parse(event.data);

                    setProgressInfo({
                        currentPage: data.currentPage,
                        totalPages: data.totalPages
                    });
                }
            }
        ]);

        loadJobRef.current?.({});
    }, [props.jobId, loadJobRef]);

    const handleTabClick = (index: number) => {
        if (!isAddMode) {
            setSelectedPage(null);
        }
        setSelectedResult(index);
    };

    const handlePageClick = (surgeryId: string, documentId: string, documentTypeId: string, index: number) => {
        setSelectedPage(index === selectedPage ? null : index);
        setIsAddMode(false);
        setMovePage({
            resultIndex: selectedResult,
            surgeryId,
            documentId,
            documentTypeId,
            page: index
        });
    };

    const handlePatientAddButtonClick = () => {
        setIsPatientSelectionDialogOpen(!isPatientSelectionDialogOpen);
    };

    const handleNewPatientSelected = (id: string, patient: any) => {
        const results = importResults;
        results.results.push({
            patient: patient,
            surgeries: [{
                surgeryId: "",
                documents: []
            }]
        });

        setIsPatientSelectionDialogOpen(false);
        setImportResults({ ...results });
        setSelectedResult(results.results.length - 1);
    };

    const handleSurgeryAddButtonClick = () => {
        setIsSurgerySelectionDialogOpen(!isSurgerySelectionDialogOpen);
    };

    const handleSurgerySelected = (surgery: any) => {
        const results = importResults;
        const result = results.results[selectedResult];
        if (!result) {
            return;
        }

        result.surgeries.unshift({
            surgeryId: surgery.id,
            date: surgery.date,
            time: surgery.time,
            documents: []
        });

        setIsSurgerySelectionDialogOpen(false);
        setImportResults({ ...results });
        setHasChanged(true);
    };

    const handleDocumentAddButtonClick = (surgeryId?: string) => {
        setDocumentSurgeryId(surgeryId || "");
        setIsDocumentSelectionDialogOpen(!isDocumentSelectionDialogOpen);
    };

    const handleDocumentTypeSelected = (documentType: any) => {
        const results = importResults;
        const result = results.results[selectedResult];
        if (!result) {
            return;
        }

        const surgery = result.surgeries.find((a: any) => a.surgeryId === documentSurgeryId);
        if (!surgery) {
            setIsDocumentSelectionDialogOpen(false);
            return;
        }

        surgery.documents.unshift({
            id: Date.now().toString(),
            documentTypeId: documentType.id,
            documentName: documentType.name,
            pages: [],
            surgeryId: documentSurgeryId || null
        });

        setIsDocumentSelectionDialogOpen(false);
        setImportResults({ ...results });
        setHasChanged(true);
    };

    const handleMovePageClick = () => {
        setIsAddMode(true);
    }

    const handleMoveDocumentClick = (surgeryId: string, documentTypeId: string, documentId: string) => {
        const results = importResults;
        const result = results.results[selectedResult];
        if (!result) {
            return;
        }

        const surgery = result.surgeries.find((a: any) => a.surgeryId === surgeryId);
        if (!surgery) {
            setIsAddMode(false);
            setMovePage(null);
            return;
        }

        const document = surgery.documents.find((a: any) => a.documentTypeId === documentTypeId && a.id === documentId);
        if (!document) {
            setIsAddMode(false);
            setMovePage(null);
            return;
        }

        const sourceDocument = results.results[movePage.resultIndex].surgeries
            .find((a: any) => a.surgeryId === movePage.surgeryId).documents
            .find((a: any) => a.documentTypeId === movePage.documentTypeId && a.id === movePage.documentId);

        document.pages.push(movePage.page);
        sourceDocument.pages.splice(sourceDocument.pages.indexOf(movePage.page), 1);

        setSelectedPage(null);
        setImportResults({ ...results });
        setMovePage(null);
        setIsAddMode(false);
        setHasChanged(true);
    }

    const handleFinishButtonClick = () => {
        const documentList = [];

        for (const result of importResults.results) {
            if (result.patient.id === "0") {
                // Ignore anything left in the unidentified section
                continue;
            }

            for (const surgery of result.surgeries) {
                for (const document of surgery.documents) {
                    if (document.pages.length === 0) {
                        continue;
                    }

                    documentList.push({
                        patientId: result.patient.id,
                        documentTypeId: document.documentTypeId,
                        surgeryId: document.surgeryId,
                        pages: document.pages
                    });
                }
            }
        }

        setIsFinishing(true);
        V2.post(`document/import/${props.jobId}/finish`, { entries: documentList })
            .catch(e => {
                setIsFinishing(e.message || "Could not finish job");
            });
    }

    const handleCancelButtonClick = () => {
        setIsFinishing(true);
        V2.delete(`document/import/${props.jobId}`)
            .then(() => {
                history.goBack();
            })
            .catch(e => {
                setIsFinishing(e.message || "Could not finish job");
            });
    };

    const handleDeletePageClick = () => {
        const results = importResults;
        let resultOffset = 0;
        if (results.results[0]?.patient?.id !== "0") {
            // Add "Unidentified pages" tab
            results.results.unshift({
                patient: {
                    id: "0",
                    chartNumber: "__unknown"
                },
                surgeries: [{
                    surgeryId: "",
                    documents: [{
                        documentTypeId: "?",
                        documentName: "Unidentified",
                        pages: importResults.unidentifiedPages,
                        surgeryId: null
                    }]
                }]
            });

            resultOffset = 1;
        }

        const unidentDocuments = results.results[0].surgeries[0].documents;
        const sourceDocument = results.results[movePage.resultIndex + resultOffset].surgeries
            .find((a: any) => a.surgeryId === movePage.surgeryId).documents
            .find((a: any) => a.documentTypeId === movePage.documentTypeId && a.id === movePage.documentId);

        unidentDocuments[0].pages.push(movePage.page);
        sourceDocument.pages.splice(sourceDocument.pages.indexOf(movePage.page), 1);

        setSelectedPage(null);
        setImportResults({ ...results });
        setMovePage(null);
        setSelectedResult(selectedResult + resultOffset);
        setHasChanged(true);
    }

    // Specifying a key on EventStream prevents it from remounting during loading, which causes event registration issues
    const streamHandler = <EventStream key="eventStreamHandler" channels={eventStreams} debugId="Import Editor" />;

    if (isLoading === true) {
        return <div className={className}>
            {streamHandler}
            <div className={Style.loadingBox}>
                <div className="spinner spinner-center lds-dual-ring-big" />
                <br />
                <span>Processing documents...</span>
            </div>
        </div>;
    }
    if (typeof isLoading === "string") {
        return <div className={className}>
            {streamHandler}
            <div className="error-text">
                {isLoading}
            </div>
        </div>;
    }

    return <div className={className}>
        <Prompt when={hasChanged} message="Your changes will not be saved if you continue." />
        {streamHandler}
        <PatientSelectionDialog
            isOpen={isPatientSelectionDialogOpen}
            onPatientSelected={handleNewPatientSelected}
            onRequestClose={handlePatientAddButtonClick} />
        <SurgerySelectionDialog
            isOpen={isSurgerySelectionDialogOpen}
            patientId={importResults.results?.[selectedResult]?.patient.id || ""}
            onSurgerySelected={handleSurgerySelected}
            onRequestClose={handleSurgeryAddButtonClick} />
        <DocumentSelectionDialog
            isOpen={isDocumentSelectionDialogOpen}
            onDocumentTypeSelected={handleDocumentTypeSelected}
            onRequestClose={handleDocumentAddButtonClick} />

        <div className={Style.tabRow}>
            {importResults.results
                ? importResults.results.map((a, i) => a.patient.chartNumber === "__unknown"
                    ? <DocumentImportEditorTab
                        key={a.patient.chartNumber}
                        icon="icon-question"
                        isSelected={i === selectedResult}
                        onClick={() => handleTabClick(i)}>
                        <div>Unidentified pages</div>
                        <small>{importResults.unidentifiedPages.length} page(s)</small>
                    </DocumentImportEditorTab>
                    : <DocumentImportEditorTab
                        key={a.patient.chartNumber}
                        patient={a.patient}
                        icon="icon-user"
                        isSelected={i === selectedResult}
                        onClick={() => handleTabClick(i)} />)
                : null}
            {!isComplete ? <DocumentImportViewerButton icon="icon-user-plus" title="Add patient" onClick={handlePatientAddButtonClick} /> : null}
        </div>
        <div className={Style.controlRow}>
            <DocumentImportViewerButton icon="icon-mail-forward" title="Move" isDisabled={selectedPage === null} onClick={handleMovePageClick} />
            <DocumentImportViewerButton icon="icon-trash" title="Move to Unidentified" isDisabled={selectedPage === null} onClick={handleDeletePageClick} />
        </div>
        <DocumentImportEditorList
            jobId={props.jobId} surgeries={importResults.results?.[selectedResult]?.surgeries || []}
            selectedPage={selectedPage}
            allowAdditions={!isComplete && importResults.results?.[selectedResult]?.patient.chartNumber !== "__unknown"}
            isMoveMode={isAddMode}
            onAddSurgeryClick={handleSurgeryAddButtonClick}
            onAddDocumentClick={handleDocumentAddButtonClick}
            onDocumentClick={handleMoveDocumentClick}
            onSelectPage={handlePageClick} />
        <div className={Style.pageViewer}>
            {isAddMode
                ? <div className={Style.notificationBanner}>Select the target document to move this page to</div>
                : null}
            {selectedPage !== null
                ? <DocumentPageImage jobId={props.jobId} page={selectedPage} dpi={96} />
                : isComplete
                    ? <div className={Style.noPageMessage}>
                        <i className="icon-check" style={{ color: "#0F3" }} />
                        <div>
                            Import complete<br />
                            <br />
                            <button type="button" className="button" onClick={() => history.goBack()}>Ok</button>
                        </div>
                    </div>
                    : importResults.status < 3
                        ? <div className={Style.noPageMessage}>
                            <i className="icon-file-pdf-o" />
                            <div>
                                <ProgressBar value={((progressInfo.currentPage + 1) * 100) / progressInfo.totalPages} />
                                Processing page {progressInfo.currentPage + 1} of {progressInfo.totalPages}<br />
                            </div>
                        </div>
                        : importResults.status === 4
                            ? <div className={Style.noPageMessage}>
                                <i className="icon-warning" />
                                <div className="error-text">{isFinishing}</div>
                                <div>
                                    <br />
                                    <button type="button" className="button red text-with-icon" onClick={handleCancelButtonClick}><i className="icon-trash" /><span>Cancel Import</span></button>
                                </div>
                            </div>
                            : <div className={Style.noPageMessage}>
                                <i className="icon-file-o" />
                                <div>
                                    Select a page on the left to view it.<br />
                                Or if you're finished you can click the button below<br />
                                    <br />
                                    {isFinishing === true
                                        ? <span className="spinner lds-dual-ring" />
                                        : <>
                                            <button type="button" className="button text-with-icon" onClick={handleFinishButtonClick}><i className="icon-check" /><span>Finish Import</span></button>
                                            <button type="button" className="button red text-with-icon" onClick={handleCancelButtonClick}><i className="icon-trash" /><span>Cancel Import</span></button>
                                        </>}
                                    {typeof isFinishing === "string"
                                        ? <div className="error-text">{isFinishing}</div>
                                        : null}
                                </div>
                            </div>}
        </div>
    </div>;
}

interface IDocumentImportViewerButtonProps {
    icon: string;
    title: string;
    isDisabled?: boolean;

    onClick: () => void;
}

const DocumentImportViewerButton: React.FC<IDocumentImportViewerButtonProps> = function (props) {
    return <button className={Style.toolbarButton} title={props.title} onClick={props.onClick} disabled={props.isDisabled}>
        <i className={`${Style.icon} ${props.icon}`} />
    </button>;
}

interface IDocumentImportEditorTabProps {
    patient?: any;
    isSelected?: boolean;
    icon?: string;
    onClick?: () => void;
}

const DocumentImportEditorTab: React.FC<IDocumentImportEditorTabProps> = function (props) {
    return <div className={props.isSelected ? `${Style.documentTab} ${Style.tabSelected}` : Style.documentTab} onClick={props.onClick} >
        {props.icon
            ? <i className={props.icon} />
            : null}
        {props.patient
            ? <div>
                <small>#{props.patient.chartNumber}</small>
                <div>{formatName(props.patient.name)}</div>
            </div>
            : <div>{props.children}</div>}
    </div>;
}

interface IDocumentImportEditorListProps {
    jobId: string;
    surgeries: DocumentImportDocumentList;
    selectedPage: number | null;
    allowAdditions: boolean;
    isMoveMode: boolean;

    onSelectPage?: (surgeryId: string, documentId: string, documentTypeId: string, page: number) => void;
    onAddSurgeryClick?: () => void;
    onAddDocumentClick?: (surgeryId: string) => void;
    onDocumentClick?: (surgeryId: string, documentTypeId: string, documentId: string) => void;
}

const DocumentImportEditorList: React.FC<IDocumentImportEditorListProps> = function (props) {
    return <div className={Style.pageList}>
        <ul className={Style.pageListGroupList}>
            {props.allowAdditions
                ? <div className={Style.listTopButton}>
                    <button className={Style.toolbarButton} onClick={props.onAddSurgeryClick}><i className={`${Style.icon} icon-calendar-plus-o`} />Add Surgery</button>
                </div>
                : null}
            {props.surgeries
                ? props.surgeries.map(s => <li key={s.surgeryId}>
                    {s.date
                        ? <div className={Style.groupTitle2}>{s.time
                            ? formatDateTimeLocalNumeric(`${s.date.substring(0, 10)}T${s.time}`)
                            : formatDateLocalNumeric(s.date)}</div>
                        : s.surgeryId === "" && props.allowAdditions
                            ? <div className={Style.groupTitle2}>General Chart Documents</div>
                            : null}
                    {props.allowAdditions
                        ? <div className={Style.listTopButton}>
                            <button className={Style.toolbarButton} onClick={() => props.onAddDocumentClick?.(s.surgeryId)}><i className={`${Style.icon} icon-plus`} />Add Document</button>
                        </div>
                        : null}
                    <ul>
                        {s.documents.map((a: any) => <li key={a.documentTypeId + a.id}>
                            <div className={props.isMoveMode ? `${Style.groupTitle} clickable` : Style.groupTitle} onClick={props.isMoveMode ? () => props.onDocumentClick?.(s.surgeryId, a.documentTypeId, a.id) : undefined}>{a.documentName}<br /><small>{a.pages.length} page(s)</small></div>
                            <ul className={Style.groupPageList}>
                                {a.pages.map((b: number) => <li key={b}>
                                    <DocumentPageImage
                                        jobId={props.jobId} page={b}
                                        isSelected={props.selectedPage === b}
                                        onClick={() => props.onSelectPage?.(s.surgeryId, a.id, a.documentTypeId, b)} />
                                </li>)}
                            </ul>
                        </li>)}
                    </ul>
                </li>)
                : null}
        </ul>
    </div>;
}

interface IDocumentPageImageProps {
    jobId: string;
    page: number;
    dpi?: number;
    isSelected?: boolean;

    onClick?: () => void;
}

const DocumentPageImage: React.FC<IDocumentPageImageProps> = function (props) {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        let isCancel = false;
        V2.get<Blob>(`document/import/${props.jobId}/pages/${props.page}?dpi=${props.dpi || 18}`)
            .then(blob => {
                if (isCancel) {
                    return;
                }

                setIsLoading(false);
                const url = URL.createObjectURL(blob);
                setBlobUrl(url);
            })
            .catch(e => {
                if (isCancel) {
                    return;
                }

                setIsLoading(false);
                setBlobUrl(null);
            });

        return () => {
            isCancel = true;
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
        // The compiler complains that 'blobUrl' isn't included, but we don't need to re-run when it changes, as we
        //   are the only ones to set it
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.jobId, props.page, props.dpi]);

    return <div className={props.isSelected === true ? `${Style.pageContainer} ${Style.pageSelected}` : Style.pageContainer} onClick={props.onClick} >
        {isLoading === true
            ? <div className="spinner spinner-center lds-dual-ring" />
            : blobUrl
                ? <img src={blobUrl} alt="PDF page" />
                : null}
    </div>;
}

interface IPatientSelectionDialogProps {
    isOpen: boolean;
    onPatientSelected: (id: string, data: any) => void;
    onRequestClose: () => void;
}

const PatientSelectionDialog: React.FC<IPatientSelectionDialogProps> = function (props) {
    if (!props.isOpen) {
        return null;
    }

    return <ReactModal
        isOpen={props.isOpen} onRequestClose={props.onRequestClose}
        overlayClassName="modal-overlay" className="modal-content modal-content-900" portalClassName="modal-portal"
        bodyOpenClassName="modal-open"
        contentLabel="Select Patient">
        <PatientSearchInner onPatientSelected={props.onPatientSelected} />
    </ReactModal>;
}

interface ISurgerySelectionDialogProps {
    isOpen: boolean;
    patientId: string;

    onSurgerySelected: (surgery: any) => void;
    onRequestClose: () => void;
}

const SurgerySelectionDialog: React.FC<ISurgerySelectionDialogProps> = function (props) {
    const [surgeries, setSurgeries] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean | string>(true);

    useEffect(() => {
        if (!props.isOpen) {
            return;
        }

        V2.get<any>(`patients/${props.patientId}/surgeries`)
            .then(result => {
                setSurgeries(result.surgeries);
                setIsLoading(false);
            })
            .catch(e => {
                setIsLoading(e.message || "Could not load surgery list");
            });
    }, [props.patientId, props.isOpen]);

    if (!props.isOpen) {
        return null;
    }

    return <ReactModal
        isOpen={props.isOpen} onRequestClose={props.onRequestClose}
        overlayClassName="modal-overlay" className="modal-content modal-content-700" portalClassName="modal-portal"
        bodyOpenClassName="modal-open"
        contentLabel="Select Surgery">
        <div className="info-block">
            <header>
                <h3>Select surgery</h3>
            </header>
            {isLoading === true ?
                <div className="spinner lds-dual-ring" />
                : typeof isLoading === "string"
                    ? <div className="error-text">{isLoading}</div>
                    : surgeries.length === 0
                        ? <div>No surgeries found for this patient</div>
                        : <table className="table-vertical">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Operation</th>
                                    <th>Surgeon</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {surgeries.map(a => <tr key={a.id} className="clickable" tabIndex={0} onClick={() => props.onSurgerySelected?.(a)}>
                                    <td>{formatDateLocalNumeric(a.date)}</td>
                                    <td>{a.primarySurgery.shortName || a.primarySurgery.name}</td>
                                    <td>{formatName(a.primarySurgeon.name)}</td>
                                    <td>{surgeryStatusToText(a.status)}</td>
                                </tr>)}
                            </tbody>
                        </table>}
        </div>
    </ReactModal>;
}

interface IDocumentSelectionDialogProps {
    isOpen: boolean;

    onDocumentTypeSelected: (documentType: any) => void;
    onRequestClose: () => void;
}

const DocumentSelectionDialog: React.FC<IDocumentSelectionDialogProps> = function (props) {
    const [documentTypes, setDocumentTypes] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean | string>(true);

    useEffect(() => {
        if (!props.isOpen) {
            return;
        }

        V2.get<any>(`document/types?show_all=true`)
            .then(result => {
                setDocumentTypes(result.documentTypes);
                setIsLoading(false);
            })
            .catch(e => {
                setIsLoading(e.message || "Could not load document types");
            });
    }, [props.isOpen]);

    if (!props.isOpen) {
        return null;
    }

    return <ReactModal
        isOpen={props.isOpen} onRequestClose={props.onRequestClose}
        overlayClassName="modal-overlay" className="modal-content modal-content-700" portalClassName="modal-portal"
        bodyOpenClassName="modal-open"
        contentLabel="Select Document Type">
        <div className="info-block">
            <header>
                <h3>Select surgery</h3>
            </header>
            {isLoading === true ?
                <div className="spinner lds-dual-ring" />
                : typeof isLoading === "string"
                    ? <div className="error-text">{isLoading}</div>
                    : <table className="table-vertical">
                        <thead>
                            <tr>
                                <th>Document</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documentTypes.map(a => <tr key={a.id} className="clickable" tabIndex={0} onClick={() => props.onDocumentTypeSelected?.(a)}>
                                <td>
                                    <div>{a.name}</div>
                                    <small>{a.description}</small>
                                </td>
                            </tr>)}
                        </tbody>
                    </table>}
        </div>
    </ReactModal>;
}

export default withContentStyle("noborder", DocumentImportEditor);
